import { Scene, WebGLRenderer, LinearSRGBColorSpace } from "three";
import LightRaysOverlay from "./scene/LightRaysOverlay.js";
import Camera from "./scene/Camera.js";
import Lighting from "./scene/Lighting.js";
import BlurSystem from "./scene/BlurSystem.js";
import OverlayPlanes from "./scene/OverlayPlanes.js";
import Coins from "./scene/Coins.js";
import { setObjectLayersDeep } from "./scene/utils.js";
import { OVERLAY_PLANE_GROUP_Z } from "./scene/constants.js";

const NORMAL_RENDER_DPR_CAP = 2;
const ZOOM_RENDER_DPR_CAP = 2.5;
const EXTREME_ZOOM_RENDER_DPR_CAP = 3;
const ZOOM_RENDER_DPR_THRESHOLD = 2;
const EXTREME_ZOOM_RENDER_DPR_THRESHOLD = 3.5;
const MAX_ZOOM_RENDER_PIXELS = 8_500_000;
const MAX_TEXTURE_ANISOTROPY = 8;

class SceneManager {
	scene = new Scene();
	camera = null; // Will be set from Camera module
	renderer = null;
	canvas = null;
	coins = []; // Will be set from Coins module
	canvasRect = null;
	winningCoin = null;
	lightRaysOverlay = null; // Light rays overlay manager

	// Module instances
	_camera = null;
	_lighting = null;
	_blurSystem = null;
	_overlayPlanes = null;
	_coins = null;

	prefersReducedMotion = false;

	constructor(canvas, prefersReducedMotion = false) {
		this.canvas = canvas;
		this.prefersReducedMotion = prefersReducedMotion;
		this.canvasRect = canvas.getBoundingClientRect();
		this.renderer = new WebGLRenderer({
			canvas,
			antialias: true,
			alpha: true,
			backgroundColor: 0x000000,
			colorSpace: LinearSRGBColorSpace,
		});

		// Initialize camera immediately so it's available for InteractionManager
		this._camera = new Camera();
		this.camera = this._camera.camera;
	}

	getRawDevicePixelRatio() {
		return window.devicePixelRatio || 1;
	}

	getRenderPixelRatio(canvasRect = this.canvasRect) {
		const rawDpr = this.getRawDevicePixelRatio();

		let targetDpr = Math.min(rawDpr, NORMAL_RENDER_DPR_CAP);
		if (rawDpr > EXTREME_ZOOM_RENDER_DPR_THRESHOLD) {
			targetDpr = Math.min(rawDpr, EXTREME_ZOOM_RENDER_DPR_CAP);
		} else if (rawDpr > ZOOM_RENDER_DPR_THRESHOLD) {
			targetDpr = Math.min(rawDpr, ZOOM_RENDER_DPR_CAP);
		}

		if (
			rawDpr <= ZOOM_RENDER_DPR_THRESHOLD ||
			!canvasRect?.width ||
			!canvasRect?.height
		) {
			return targetDpr;
		}

		const maxBudgetedDpr = Math.sqrt(
			MAX_ZOOM_RENDER_PIXELS / (canvasRect.width * canvasRect.height)
		);

		return Number(
			Math.max(
				NORMAL_RENDER_DPR_CAP,
				Math.min(targetDpr, maxBudgetedDpr)
			).toFixed(2)
		);
	}

	getEffectsPixelRatio(renderDpr) {
		return Math.min(renderDpr, NORMAL_RENDER_DPR_CAP);
	}

	getCoinTextureAnisotropy() {
		const maxAnisotropy =
			typeof this.renderer?.capabilities?.getMaxAnisotropy === "function"
				? this.renderer.capabilities.getMaxAnisotropy()
				: 1;

		return Math.max(1, Math.min(MAX_TEXTURE_ANISOTROPY, maxAnisotropy));
	}

	updateCanvasSize() {
		// Force a reflow to ensure canvas has updated size in DOM
		void this.canvas.offsetWidth;

		// Get fresh bounding rect - this gives us the actual rendered size
		this.canvasRect = this.canvas.getBoundingClientRect();
		const renderDpr = this.getRenderPixelRatio(this.canvasRect);
		const effectsDpr = this.getEffectsPixelRatio(renderDpr);

		// Ensure we have valid dimensions
		const width = this.canvasRect.width;
		const height = this.canvasRect.height;

		if (width === 0 || height === 0) {
			return;
		}

		// Update camera aspect ratio
		if (this._camera) {
			this._camera.updateAspectRatio(width, height);
		}

		// Update renderer size - this updates the canvas element's internal width/height attributes
		// The third parameter (false) means don't update CSS style, which is correct since CSS handles sizing
		this.renderer.setSize(width, height, false);
		this.renderer.setPixelRatio(renderDpr);

		// Update blur system size
		if (this._blurSystem) {
			this._blurSystem.updateSize(this.canvasRect, effectsDpr);
		}

		// Update overlay planes size
		if (this._overlayPlanes && this._camera) {
			this._overlayPlanes.updateSize(this._camera.camera, this.canvasRect);
		}

		// Update light rays overlay canvas size
		if (this.lightRaysOverlay) {
			this.lightRaysOverlay.updateCanvasSize(this.canvasRect);
		}

		if (this._coins) {
			void this._coins.updateTextureQuality({
				devicePixelRatio: this.getRawDevicePixelRatio(),
				textureAnisotropy: this.getCoinTextureAnisotropy(),
			});
		}
	}

	async setupScene() {
		this.updateCanvasSize();

		// Setup camera (already initialized in constructor, just configure it)
		this._camera.setup();

		// Setup lighting
		this._lighting = new Lighting();
		this._lighting.setup(this.scene, this._camera.sharedLayer);

		// Setup blur system
		const renderDpr = this.getRenderPixelRatio(this.canvasRect);
		const effectsDpr = this.getEffectsPixelRatio(renderDpr);
		this._blurSystem = new BlurSystem();
		this._blurSystem.setup(this.canvasRect, effectsDpr);

		// Setup overlay planes
		this._overlayPlanes = new OverlayPlanes();
		this._overlayPlanes.setup(
			this._camera.camera,
			this.canvasRect,
			this._blurSystem.finalBlurTexture,
			this._camera.foregroundLayer
		);
		this.scene.add(this._overlayPlanes.overlayPlaneGroup);

		// Setup light rays overlay
		this.lightRaysOverlay = new LightRaysOverlay(this._camera.camera, this.canvasRect);
		this.lightRaysOverlay.setup(
			this._overlayPlanes.overlayPlaneGroup,
			this._camera.foregroundLayer
		);

		// Setup coins (async - waits for fonts to load before rendering textures)
		this._coins = new Coins();
		await this._coins.setup(this.scene, this.canvas, this._camera.foregroundLayer, {
			devicePixelRatio: this.getRawDevicePixelRatio(),
			textureAnisotropy: this.getCoinTextureAnisotropy(),
		});
		this.coins = this._coins.coins;
	}

	setWinningCoin(coin) {
		this.winningCoin = coin;
		// Update light rays overlay to track winning coin position
		if (this.lightRaysOverlay) {
			this.lightRaysOverlay.setWinningCoin(coin);
		}
	}

	setBlurAmount(amount) {
		if (this._blurSystem) {
			this._blurSystem.setBlurAmount(amount);
		}
		if (this._overlayPlanes) {
			this._overlayPlanes.setBlurAmount(amount);
		}
	}

	/**
	 * Update rays animation
	 * @param {number} deltaTime - Time delta in milliseconds
	 */
	updateRays(deltaTime) {
		// Skip ray animation if user prefers reduced motion
		if (this.prefersReducedMotion) {
			return;
		}
		if (this.lightRaysOverlay && this._blurSystem) {
			this.lightRaysOverlay.update(deltaTime, this._blurSystem.blurAmount);
		}
	}

	updateCoins({
		wheelRotation,
		mousePos,
		timeDelta,
		state,
		revealAnimationManager = null,
		spinsPerSecond = 0,
		contentRect = null,
	}) {
		let coinScale = 1;

		// Update camera and coin orbit radius based on wheel speed and state
		if (this._camera && this._coins) {
			const speedEffects = this._camera.updateSpeedEffects(
				spinsPerSecond,
				state,
				contentRect,
				this.canvasRect
			);
			const animatedCoin = revealAnimationManager?.getAnimatedCoin();
			coinScale = speedEffects.coinScale;
			this._coins.setOrbitRadius(speedEffects.orbitRadius, animatedCoin);
		}

		// Update coins
		if (this._coins) {
			this._coins.update({
				wheelRotation,
				mousePos,
				timeDelta,
				state,
				revealAnimationManager,
				spinsPerSecond,
				layoutScale: coinScale,
				prefersReducedMotion: this.prefersReducedMotion,
			});
		}
	}

	render() {
		if (
			this._blurSystem &&
			this._blurSystem.blurAmount > 0 &&
			this.winningCoin &&
			this._blurSystem.backgroundRenderTarget
		) {
			// Set winning coin to foreground layer
			setObjectLayersDeep(this.winningCoin.container, this._camera.foregroundLayer);

			// Set background coins to background layer
			this.coins.forEach((coin) => {
				if (coin !== this.winningCoin) {
					setObjectLayersDeep(coin.container, this._camera.backgroundLayer);
				}
			});

			// Set camera to render shared layer (lights) and background layer
			this.camera.layers.enable(this._camera.sharedLayer);
			this.camera.layers.enable(this._camera.backgroundLayer);
			this.camera.layers.disable(this._camera.foregroundLayer);

			// Render background to render target
			this.renderer.setRenderTarget(this._blurSystem.backgroundRenderTarget);
			this.renderer.clear();
			this.renderer.render(this.scene, this.camera);

			// Restore camera to render all layers
			this.camera.layers.enable(this._camera.sharedLayer);
			this.camera.layers.disable(this._camera.backgroundLayer);
			this.camera.layers.enable(this._camera.foregroundLayer);

			// Apply blur
			this._blurSystem.applyBlur(this.renderer);

			// Get the final blurred texture
			const blurredTexture = this._blurSystem.finalBlurTexture;

			// Update overlay planes with blurred texture
			if (this._overlayPlanes) {
				// Update plane size based on distance from camera to overlay plane group
				this._overlayPlanes.updateSize(this._camera.camera, this.canvasRect);

				// Update plane positions and orientations
				this._overlayPlanes.updateOrientation(this._camera.camera);

				// Update the texture
				this._overlayPlanes.updateTexture(blurredTexture);

				// Update light rays overlay plane size
				if (this.lightRaysOverlay) {
					// Calculate plane size for light rays overlay
					const distance = this._camera.camera.position.z - OVERLAY_PLANE_GROUP_Z;
					const fov = this._camera.camera.fov * (Math.PI / 180);
					const height = 2 * Math.tan(fov / 2) * distance;
					const aspect = this.canvasRect.width / this.canvasRect.height;
					const width = height * aspect;
					this.lightRaysOverlay.updatePlaneSize(width, height);
					this.lightRaysOverlay.updateOrientation();
				}

				// Scale up group to full size
				this._overlayPlanes.setScale(1);
			}

			// Render to main canvas: background + winning coin
			this.renderer.setRenderTarget(null);
			this.renderer.clear();
			this.renderer.render(this.scene, this.camera);
		} else {
			// Normal rendering without blur - scale group down to very small
			if (this._overlayPlanes) {
				this._overlayPlanes.setScale(0.001);
			}

			// Ensure all coins are on foreground layer for normal rendering
			if (this._camera) {
				this.coins.forEach((coin) => {
					setObjectLayersDeep(coin.container, this._camera.foregroundLayer);
				});
			}

			this.renderer.setRenderTarget(null);
			this.renderer.render(this.scene, this.camera);
		}
	}
}

export default SceneManager;
